import React from 'react'
import moment from 'moment'
import type {GpsStatus} from '../VehicleMonitoring/VehicleList'

export type AvailableVehicleDetailData = {
  itemId: number
  vehicleNumber: string
  vehicleType?: string | null
  baseLocation?: string | null
  speed: number | null
  gpsStatus: GpsStatus
  lastUpdateUnix?: number | null
  jobCount?: number | null
  totalKM?: number | null
  revenue?: number | null
}

type Props = {
  open: boolean
  variant: 'drawer' | 'sheet'
  vehicle: AvailableVehicleDetailData | null
  statsLoading?: boolean
  isFollowing: boolean
  onClose: () => void
  onFocus: () => void
  onToggleFollow: () => void
}

const statusLabel = (status: GpsStatus) => {
  if (status === 'moving') return 'Moving'
  if (status === 'stopped') return 'Stopped'
  return 'No GPS'
}

const statusClass = (status: GpsStatus) => {
  if (status === 'moving') return 'vm-status-chip vm-status-chip--moving'
  if (status === 'stopped') return 'vm-status-chip vm-status-chip--stopped'
  return 'vm-status-chip vm-status-chip--no-gps'
}

function formatStatValue(
  value: number | null | undefined,
  statsLoading: boolean,
  format?: (n: number) => string
): string {
  if (statsLoading) return '…'
  if (value == null) return '—'
  return format ? format(value) : String(value)
}

export const AvailableVehicleDetailPanel: React.FC<Props> = ({
  open,
  variant,
  vehicle,
  statsLoading = false,
  isFollowing,
  onClose,
  onFocus,
  onToggleFollow,
}) => {
  if (!open || !vehicle) return null

  const lastUpdate =
    vehicle.lastUpdateUnix != null ? moment.unix(vehicle.lastUpdateUnix) : null

  return (
    <div
      className={[
        'vm-detail-panel',
        variant === 'drawer' ? 'vm-detail-panel--drawer' : 'vm-detail-panel--sheet',
      ].join(' ')}
    >
      <div className='vm-detail-panel__header'>
        <div className='min-w-0 flex-grow-1'>
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            <h4 className='fs-6 fw-bold text-gray-900 mb-0 text-truncate'>
              {vehicle.vehicleNumber}
            </h4>
            <span className={statusClass(vehicle.gpsStatus)}>
              {statusLabel(vehicle.gpsStatus)}
            </span>
          </div>
          <div className='text-gray-600 fs-8 mt-1 text-truncate'>
            {[vehicle.vehicleType, vehicle.baseLocation].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
        <button type='button' className='btn btn-sm btn-icon btn-light' onClick={onClose}>
          <i className='bi bi-x-lg'></i>
        </button>
      </div>

      <div className='vm-detail-panel__body'>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Vehicle type</span>
          <span className='vm-detail-field__value'>{vehicle.vehicleType || '—'}</span>
        </div>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Base location</span>
          <span className='vm-detail-field__value'>{vehicle.baseLocation || '—'}</span>
        </div>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Speed</span>
          <span className='vm-detail-field__value'>
            {vehicle.speed != null ? `${vehicle.speed} km/h` : '—'}
          </span>
        </div>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Last update</span>
          <span className='vm-detail-field__value'>
            {lastUpdate
              ? `${lastUpdate.format('YYYY-MM-DD HH:mm:ss')} (${lastUpdate.fromNow()})`
              : '—'}
          </span>
        </div>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Job count</span>
          <span className='vm-detail-field__value'>
            {formatStatValue(vehicle.jobCount, statsLoading)}
          </span>
        </div>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Total KM</span>
          <span className='vm-detail-field__value'>
            {formatStatValue(vehicle.totalKM, statsLoading)}
          </span>
        </div>
        <div className='vm-detail-field'>
          <span className='vm-detail-field__label'>Revenue (LKR)</span>
          <span className='vm-detail-field__value'>
            {formatStatValue(vehicle.revenue, statsLoading, (n) =>
              n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
            )}
          </span>
        </div>
      </div>

      <div className='vm-detail-panel__actions'>
        <button type='button' className='btn btn-sm btn-light-primary' onClick={onFocus}>
          Focus
        </button>
        <button
          type='button'
          className={`btn btn-sm ${isFollowing ? 'btn-primary' : 'btn-light'}`}
          onClick={onToggleFollow}
        >
          {isFollowing ? 'Following' : 'Follow'}
        </button>
        <button type='button' className='btn btn-sm btn-light' onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  )
}
